import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded font-medium whitespace-nowrap border",
  {
    variants: {
      tone: {
        neutral: "bg-gray-3 text-gray-11 border-transparent",
        accent: "bg-accent-3 text-accent-11 border-transparent",
        success: "bg-success-2 text-success-11 border-success-4",
        warning: "bg-warning-2 text-warning-11 border-warning-4",
        danger: "bg-danger-2 text-danger-11 border-danger-4",
        outline: "bg-transparent text-text-muted border-border",
      },
      size: {
        sm: "h-4 px-1.5 text-2xs",
        md: "h-5 px-2 text-xs",
      },
    },
    defaultVariants: { tone: "neutral", size: "md" },
  },
);

export function Badge({
  className, tone, size, ...props
}: React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ tone, size }), className)} {...props} />;
}

/**
 * A registration/health dot with three states, not two.
 *
 * `null` is "we could not reach the live-state store", which must not look the same as
 * "offline" — one means check the infrastructure, the other means call the rep.
 */
export function StatusDot({
  state, className, label,
}: {
  state: boolean | null;
  className?: string;
  label?: string;
}) {
  const text = label ?? (state === null ? "Unknown" : state ? "Registered" : "Offline");
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)} title={text}>
      <span
        className={cn(
          "relative inline-block size-1.5 rounded-full",
          state === null && "bg-gray-7",
          state === true && "bg-success-9",
          state === false && "bg-gray-7 ring-1 ring-inset ring-gray-8",
        )}
        aria-hidden
      />
      <span className="sr-only">{text}</span>
    </span>
  );
}
